import Modal from 'react-modal';
import { usePost } from '../hooks/usePost';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as z from "zod";
import { SuccessResponse } from './form';

// ✅ Zod v4 schema with future date validation
const validationSchema = z.object({
  guestName: z
    .string()
    .min(3, { message: "Guest name is required" }),
  date: z
    .string()
    .refine((val) => {
      if (!val) return false;
      const inputDate = new Date(val);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return inputDate > today;
    }, { message: "Event date must be in the future" }),
  attendeesCount: z
    .string()
    .regex(/^\d+$/, { message: "Attendees must be a number" })
    .refine((val) => parseInt(val) > 0, { message: "At least 1 attendee required" }),
  location: z
    .string()
    .min(3, { message: "Location is required" }),
  email: z
    .string()
    .email({ message: "Enter a valid email" }),
  phone: z
    .string()
    .regex(/^\d+$/, { message: "Phone number must contain only digits" })
    .length(10, { message: "Phone number must be exactly 10 digits" })
    .refine(
      (val) => !/^(\d)\1{9}$/.test(val) && !/(0123456789|1234567890|9876543210)/.test(val),
      { message: "Enter a valid non-spam phone number" }
    ),
});

const BookArtistForm = ({ artistId, isOpen, closeModal }) => {
  const [response, setResponse] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValid, setIsValid] = useState(false);

  const { mutate, isPending } = usePost({
    url: '/api/book-artist',
    onSuccess: (res) => {
      // toast.success('Event attended successfully');
      setResponse(res)
      setTimeout(() => {
         closeModal();
      }, 5000);
      setFormData({});
      setErrors({});
      setTouched({});
    },
    onError: ({ response }) => {
      if (response) {
         setErrors(response.data.errors)
      } else {

      }
      console.error('Error attending event:', response);
      toast.error("Something went wrong!");
    },
  });

  // ✅ validate all fields to enable/disable submit
  useEffect(() => {
    const result = validationSchema.safeParse(formData);
    setIsValid(result.success);
  }, [formData]);

  // ✅ Validate field on blur (Zod v4-compatible)
  const handleBlur = (name) => {
    const fieldSchema = validationSchema.shape[name];
    if (!fieldSchema) return;

    const value = formData[name] ?? "";
    const result = fieldSchema.safeParse(value);

    setTouched((prev) => ({ ...prev, [name]: true }));

    setErrors((prev) => ({
      ...prev,
      [name]: result.success
        ? undefined
        : result.error.issues?.[0]?.message || "Invalid input",
    }));
  };

  const handleFieldChange = (name) => (e) => {
    setFormData((prev) => ({ ...prev, [name]: e.target.value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const result = validationSchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors = {};
      result.error.issues.forEach((issue) => {
        fieldErrors[issue.path[0]] = issue.message;
      });
      setErrors(fieldErrors);
      toast.error("Please fix the validation errors!");
      return;
    }

    setErrors({});
    const payload = new FormData(event.target)
    payload.set('artistId', artistId)
    mutate(payload);
  };

  return (
    <Modal
      isOpen={isOpen}
      className="event-attendance-modal"
      contentLabel="Book artist form"
    >
      <h2>Book Artist</h2>
      <button onClick={closeModal} className="modal__close-btn">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
      {response ? (
         <SuccessResponse message={response.message} />
      ): (
      <form onSubmit={handleSubmit}>
        {[
          { name: 'guestName', type: 'text', placeholder: 'Name of the Guest' },
          { name: 'date', type: 'date', placeholder: 'Date of the Event' },
          { name: 'attendeesCount', type: 'number', placeholder: 'No. of Attendees' },
          { name: 'location', type: 'text', placeholder: 'Location of the Event' },
          { name: 'email', type: 'email', placeholder: 'Email' },
          { name: 'phone', type: 'tel', placeholder: 'Mobile number' },
        ].map((field) => (
          <div className="mb-3 input-sec" key={field.name}>
            <input
              value={formData[field.name] || ""}
              onChange={handleFieldChange(field.name)}
              onBlur={() => handleBlur(field.name)}
              className="form-control"
              type={field.type}
              name={field.name}
              placeholder={field.placeholder}
            />
            {touched[field.name] && errors[field.name] && (
              <p className="text-danger text-sm">{errors[field.name]}</p>
            )}
          </div>
        ))}

        <button
          disabled={!isValid || isPending}
          className={`btn btn-dark ${(!isValid || isPending) ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          {isPending ? 'Submitting...' : 'Submit'}
        </button>
      </form>
      )}

    </Modal>
  );
};

export default BookArtistForm
